/* eslint-disable import/prefer-default-export */
import ISO6391 from "iso-639-1";

/**
 * Custom locale codes that extend beyond ISO 639-1 2-letter codes.
 * No ISO library is "complete", so we may add custom locale codes here.
 * Useful for specific locale variants like `es-mx`, `zh-hans`, `zh-hant`, etc.
 */
export const CUSTOM_LOCALE_CODES = {
  "zh-hant": { name: "Chinese (Traditional)", nativeName: "繁體中文" },
  "zh-hans": { name: "Chinese (Simplified)", nativeName: "简体中文" },
} satisfies Record<string, { name: string; nativeName: string }>;

type CustomLocaleCode = keyof typeof CUSTOM_LOCALE_CODES;

const isCustomLocaleCode = (code: string): code is CustomLocaleCode =>
  Object.keys(CUSTOM_LOCALE_CODES).includes(code);

/**
 * Get all available language codes (both ISO 639-1 and custom codes).
 *
 * @returns {Array<string>} Array of all available language codes
 */
export const getAllLanguageCodes = (): string[] =>
  [...ISO6391.getAllCodes(), ...Object.keys(CUSTOM_LOCALE_CODES)].sort();

/**
 * Get the English name for a language code.
 * Supports both ISO 639-1 2-letter codes and custom extended codes.
 *
 * @param {string} languageCode - The language code (e.g., 'en', 'zh-Hant', 'es')
 * @returns {string} The English name of the language
 */
export const getLanguageName = (languageCode: string): string => {
  if (isCustomLocaleCode(languageCode)) {
    return CUSTOM_LOCALE_CODES[languageCode].name;
  }
  return ISO6391.getName(languageCode);
};

/**
 * Get the native name for a language code.
 * Supports both ISO 639-1 2-letter codes and custom extended codes.
 *
 * @param {string} languageCode - The language code (e.g., 'en', 'zh-Hant', 'es')
 * @returns {string} The native name of the language
 */
export const getLanguageNativeName = (languageCode: string): string => {
  if (isCustomLocaleCode(languageCode)) {
    return CUSTOM_LOCALE_CODES[languageCode].nativeName;
  }
  return ISO6391.getNativeName(languageCode);
};
